import type { Location, ShopifyPickupOptions } from "../../api/types";
import { closedDateLabel, nextClosedDate, openState } from "../../utils/openingHours";

/**
 * G-203 redesign — the words on a shop tile and in its panel. One place, so
 * the tile and the panel head never disagree about what a shop is doing.
 */

export interface ShopPill {
  label: string;
  tone: "success" | "muted" | "warning";
}

/** "Carrer de Còrsega, 267 · Eixample" — the street with its number and the district, no city. */
export function shortAddress(address: string): string {
  const cleaned = address.replace(/^\s*direcci[oó]n:\s*/i, "").trim();
  const raw = cleaned.split(",").map((p) => p.trim()).filter(Boolean);
  // A bare number after the street is the street number, not a new part.
  const parts: string[] = [];
  for (const p of raw) {
    if (parts.length > 0 && /^\d+[a-zA-Z]?(\s*[-–]\s*\d+)?$/.test(p)) parts[parts.length - 1] += `, ${p}`;
    else parts.push(p);
  }
  const kept = parts.filter((p) => !/^\d{5}\b/.test(p) && !/^espa[ñn]a$/i.test(p));
  return kept.slice(0, 2).join(" · ");
}

export function openPill(loc: Location, now: Date): ShopPill {
  const state = openState(loc.openingHours, loc.closedDates, now);
  if (state.kind === "not-set") return { label: "Hours not set", tone: "warning" };
  if (state.kind === "open") return { label: `Open · closes ${state.closesAt}`, tone: "success" };
  return { label: `Closed · ${state.opensText}`, tone: "muted" };
}

/** The next closure within the coming month; a date further out is the panel's business. */
export const CLOSURE_PILL_DAYS = 31;

export function closurePill(loc: Location, today: string): ShopPill | null {
  const next = nextClosedDate(loc.closedDates, today, CLOSURE_PILL_DAYS);
  return next ? { label: `Closed ${closedDateLabel(next.date, today)}`, tone: "muted" } : null;
}

export function pinPill(loc: Location): ShopPill {
  return loc.lat != null && loc.lng != null
    ? { label: "Door pinned", tone: "muted" }
    : { label: "No door pin", tone: "warning" };
}

export function shopifyPill(loc: Location, pickup: ShopifyPickupOptions | null): ShopPill | null {
  if (!pickup || !pickup.connected) return null;
  const mapped =
    loc.shopifyStoreDomain === pickup.storeDomain
      ? pickup.locations.find((o) => o.id === loc.shopifyLocationId)
      : undefined;
  return mapped
    ? { label: `Shopify: ${mapped.name}`, tone: "muted" }
    : { label: "Shopify: not mapped", tone: "muted" };
}

export function countLine(locations: Location[]): string {
  const active = locations.filter((l) => l.isActive).length;
  const inactive = locations.length - active;
  const shops = `${active} ${active === 1 ? "shop" : "shops"}`;
  return inactive > 0 ? `${shops} · ${inactive} inactive` : shops;
}
